import React from 'react';
import { Icon } from 'semantic-ui-react';
import { StatusMessage } from './TryOnLoadingIndicator';
import './TryOnErrorHandler.css';

/**
 * Error handling component for virtual try-on operations
 * Provides user-friendly error messages and recovery options
 */
const TryOnErrorHandler = ({ 
  error, 
  onRetry = null, 
  onDismiss = null,
  showRetry = true,
  showDismiss = true 
}) => {
  if (!error) return null;

  const getErrorInfo = (errorMessage) => {
    const message = errorMessage.toLowerCase();
    
    if (message.includes('quota') || message.includes('limit')) {
      return {
        type: 'warning',
        title: 'Usage Limit Reached',
        description: 'You\'ve reached your daily virtual try-on limit. Please try again tomorrow or upgrade your account.',
        icon: 'warning sign',
        suggestions: [
          'Try again tomorrow when your quota resets',
          'Consider upgrading to a premium account for more tries',
          'Wait a few minutes and try again'
        ],
        canRetry: false
      };
    }
    
    if (message.includes('image quality') || message.includes('too low') || message.includes('invalid image')) {
      return {
        type: 'warning',
        title: 'Image Quality Issue',
        description: 'The image quality is too low for virtual try-on. Please upload a clearer photo.',
        icon: 'camera',
        suggestions: [
          'Use a high-resolution image (at least 512x512 pixels)',
          'Ensure good lighting in the photo',
          'Make sure the person is clearly visible',
          'Try a different photo with better quality'
        ],
        canRetry: true
      };
    }
    
    if (message.includes('person not detected') || message.includes('no person')) {
      return {
        type: 'warning',
        title: 'Person Not Detected',
        description: 'We couldn\'t detect a person in the uploaded image. Please use a photo with a clear view of a person.',
        icon: 'user',
        suggestions: [
          'Use a photo with a person clearly visible',
          'Ensure the person takes up most of the frame',
          'Avoid group photos or distant shots',
          'Make sure the person is facing the camera'
        ],
        canRetry: true
      };
    }
    
    if (message.includes('network') || message.includes('connection') || message.includes('fetch')) {
      return {
        type: 'error',
        title: 'Connection Error',
        description: 'Unable to connect to the virtual try-on service. Please check your internet connection.',
        icon: 'globe',
        suggestions: [
          'Check your internet connection',
          'Try refreshing the page',
          'Wait a moment and try again',
          'Contact support if the problem persists'
        ],
        canRetry: true
      };
    }
    
    if (message.includes('service') || message.includes('unavailable') || message.includes('server')) {
      return {
        type: 'error',
        title: 'Service Temporarily Unavailable',
        description: 'The virtual try-on service is temporarily unavailable. Please try again in a few minutes.',
        icon: 'wrench',
        suggestions: [
          'Wait a few minutes and try again',
          'Wait a few minutes and try again',
          'Check our status page for updates',
          'Contact support if the issue persists'
        ],
        canRetry: true
      };
    }
    
    if (message.includes('too large') || message.includes('file size')) {
      return {
        type: 'warning',
        title: 'File Too Large',
        description: 'The uploaded image is too large. Please use a smaller image file.',
        icon: 'folder',
        suggestions: [
          'Compress the image to reduce file size',
          'Use an image smaller than 5MB',
          'Try a different image format (JPEG recommended)',
          'Resize the image to a smaller resolution'
        ],
        canRetry: true
      };
    }
    
    // Generic error
    return {
      type: 'error',
      title: 'Something Went Wrong',
      description: 'An unexpected error occurred during virtual try-on processing.',
      icon: 'times circle',
      suggestions: [
        'Try again with different images',
        'Refresh the page and try again',
        'Wait a few minutes and try again',
        'Contact support if the problem continues'
      ],
      canRetry: true
    };
  };

  const errorInfo = getErrorInfo(error);

  return (
    <div className="try-on-error-container">
      <div className="error-header">
        <div className="error-icon"><Icon name={errorInfo.icon} size="large" style={{ margin: 0 }} /></div>
        <div className="error-content">
          <h3 className="error-title">{errorInfo.title}</h3>
          <p className="error-description">{errorInfo.description}</p>
        </div>
      </div>

      {errorInfo.suggestions && errorInfo.suggestions.length > 0 && (
        <div className="error-suggestions">
          <h4 className="suggestions-title">Suggestions:</h4>
          <ul className="suggestions-list">
            {errorInfo.suggestions.map((suggestion, index) => (
              <li key={index} className="suggestion-item">
                {suggestion}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="error-actions">
        {showRetry && errorInfo.canRetry && onRetry && (
          <button 
            className="retry-button"
            onClick={onRetry}
          >
            Try Again
          </button>
        )}
        
        {showDismiss && onDismiss && (
          <button 
            className="dismiss-button"
            onClick={onDismiss}
          >
            Dismiss
          </button>
        )}
      </div>

      {/* Technical Details (collapsible) */}
      <details className="error-details">
        <summary className="details-summary">Technical Details</summary>
        <div className="details-content">
          <code className="error-message">{error}</code>
        </div>
      </details>
    </div>
  );
};

/**
 * Inline error message component
 */
export const InlineError = ({ error, onDismiss = null }) => {
  if (!error) return null;

  return (
    <StatusMessage
      type="error"
      message={error}
      onClose={onDismiss}
    />
  );
};

/**
 * Success message component
 */
export const SuccessMessage = ({ 
  message = 'Virtual try-on completed successfully!', 
  onDismiss = null,
  autoHide = false,
  hideDelay = 3000
}) => {
  React.useEffect(() => {
    if (autoHide && onDismiss) {
      const timer = setTimeout(onDismiss, hideDelay);
      return () => clearTimeout(timer);
    }
  }, [autoHide, onDismiss, hideDelay]);

  return (
    <StatusMessage
      type="success"
      message={message}
      onClose={onDismiss}
    />
  );
};

/**
 * Warning message component
 */
export const WarningMessage = ({ 
  message, 
  onDismiss = null,
  actionLabel = null,
  onAction = null
}) => {
  return (
    <div className="warning-message-container">
      <StatusMessage
        type="warning"
        message={message}
        onClose={onDismiss}
      />
      {actionLabel && onAction && (
        <button 
          className="warning-action-button"
          onClick={onAction}
        >
          {actionLabel}
        </button>
      )}
    </div>
  );
};

export default TryOnErrorHandler;